import API from "./api";
import { SIDEBAR_KEY_ALIASES, hasEmployeePageAccess, EMPLOYEE_PAGE_KEYS, hasLeaveDashboardAccess } from "../rbac/rolePageAccess";
import { hasRole } from "../rbac/access";

export const fetchMyPageAccess = async () => {
  const { data } = await API.get("/iam/my-page-access");
  return data;
};

export function pathIsAllowed(path, allowedPaths) {
  if (!path || !Array.isArray(allowedPaths) || !allowedPaths.length) return false;
  const base = String(path).split("?")[0].replace(/\/+$/, "") || "/";
  return allowedPaths.some((p) => {
    const norm = String(p).replace(/\/+$/, "") || "/";
    return base === norm || base.startsWith(`${norm}/`);
  });
}

function resolvePageKey(itemKey) {
  if (!itemKey) return null;
  return SIDEBAR_KEY_ALIASES[itemKey] || itemKey;
}

function isAdminOnlyItem(item) {
  return (
    item?.key === "settings_security" ||
    item?.key === "settings_users_roles" ||
    item?.path === "/settings/security/users-roles"
  );
}

/** Items outside the permission template catalog — always visible. */
const ALWAYS_VISIBLE_KEYS = new Set([
  "notice_board",
  "help",
  "help_center",
  "support_center",
]);

function itemRoleAllowed(item, role) {
  if (!Array.isArray(item?.roles) || !item.roles.length) return true;
  return hasRole(role, item.roles);
}

/** Employee self-service sidebar keys — always visible for employee role. */
const EMPLOYEE_SELF_SERVICE_KEYS = new Set([
  "emp_home",
  "emp_att_my",
  "emp_leave_my",
  "emp_comp_off",
  "emp_payslip",
  "emp_fuel",
  "emp_holidays",
  "emp_my_onboarding",
  "emp_my_exit",
]);

function itemAllowedByTemplate(item, pageAccess, role) {
  if (!item) return false;
  if (!itemRoleAllowed(item, role)) return false;
  if (ALWAYS_VISIBLE_KEYS.has(item.key)) return true;
  if (isAdminOnlyItem(item)) return role === "admin";

  const normalizedRole = String(role || "").toLowerCase();
  if (normalizedRole === "employee" && EMPLOYEE_SELF_SERVICE_KEYS.has(item.key)) {
    return true;
  }

  const pageKeys = new Set(pageAccess?.page_keys || []);
  const allowedPaths = pageAccess?.allowed_paths || [];
  const resolvedKey = resolvePageKey(item.key);

  // Unified Employees module — always visible to admin/hr
  if (EMPLOYEE_PAGE_KEYS.has(item.key) && hasRole(role, ["admin", "hr"])) return true;
  if (item.key === "people_hub") {
    if (hasRole(role, ["admin", "hr", "manager", "finance"])) {
      return (
        pageKeys.has("people_employees")
        || pageKeys.has("emp_hierarchy")
        || pageKeys.has("lifecycle")
        || pageKeys.has("mgr_team")
        || hasEmployeePageAccess(pageAccess)
      );
    }
    return false;
  }
  if (item.key === "people_employees" && hasRole(role, ["admin", "hr"])) return true;
  if (item.key === "lv_comp_off" || item.key === "emp_comp_off") {
    if (pageKeys.has("lv_comp_off") || pageKeys.has("lv_dashboard") || pageKeys.has("lv_apply") || pageKeys.has("lv_balance")) {
      return true;
    }
  }
  if (item.key === "lv_dashboard") {
    if (hasRole(role, ["admin", "hr", "manager"])) return true;
    if (hasLeaveDashboardAccess(pageAccess)) return true;
  }
  if (item.key === "lv_calendar" && pageKeys.has("lv_calendar")) return true;

  // Merged Payroll Hub — visible if Run Payroll or Pay Periods access is granted
  if (
    (item.key === "pay_process" ||
      item.key === "pay_process_hub" ||
      item.key === "pay_payroll_calendar") &&
    (pageKeys.has("pay_process") ||
      pageKeys.has("pay_periods") ||
      pathIsAllowed("/payroll-process", allowedPaths) ||
      pathIsAllowed("/process-payroll", allowedPaths) ||
      pathIsAllowed("/pay-periods", allowedPaths) ||
      pathIsAllowed("/salary-v2/payroll-calendar", allowedPaths))
  ) {
    return true;
  }

  if (resolvedKey && pageKeys.has(resolvedKey)) return true;
  if (item.key && pageKeys.has(item.key)) return true;
  if (item.key === "people_employees" && hasEmployeePageAccess(pageAccess)) return true;
  if (EMPLOYEE_PAGE_KEYS.has(item.key) && hasEmployeePageAccess(pageAccess)) return true;
  if (item.path && pathIsAllowed(item.path, allowedPaths)) return true;

  return false;
}

export function filterSidebarByPageAccess(items, pageAccess, role) {
  if (!Array.isArray(items)) return [];

  return items
    .map((it) => {
      if (!itemRoleAllowed(it, role)) return null;

      const children = Array.isArray(it.children)
        ? filterSidebarByPageAccess(it.children, pageAccess, role)
        : undefined;

      if (children) {
        if (!children.length) return null;
        return { ...it, children };
      }

      // When IAM templates are not loaded yet, show all leaf items
      if (!pageAccess?.page_keys?.length) return it;

      return itemAllowedByTemplate(it, pageAccess, role) ? it : null;
    })
    .filter(Boolean);
}

export function filterQuickActionsByPageAccess(actions, pageAccess) {
  if (!pageAccess?.allowed_paths?.length) return actions;
  return (actions || []).filter((action) => {
    const path = action?.action?.path;
    if (!path) return true;
    return pathIsAllowed(path, pageAccess.allowed_paths);
  });
}
